import React, { useState, useEffect } from "react";
import { Fab } from "@material-ui/core";
import { toast } from "react-toastify";
import { Link, Route, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import BlockIcon from "@material-ui/icons/Block";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import PropTypes from "prop-types";

import SendIcon from "@material-ui/icons/Send";
import moment from "moment";

import ConfirmationDialog from "@components/ConfirmationDialog";
import CrudTable from "@components/CrudTable";
import PageBody from "@components/PageBody";
import AdminPageTemplate from "@templates/AdminPage";
import User from "@services/User";
import UserUtil from "@utils/user";
import UsersMaintenanceDialog from "@src/components/UsersMaintenanceDialog";
import { USER_DATETIME_FORMAT } from "@utils/datetime";

const useStyles = makeStyles(() => ({
  badge: {
    "& .MuiBadge-dot": {
      marginTop: "-4px",
      marginRight: "-4px",
    },
  },
  buttons: {
    marginBottom: "20px",
  },
  control: {
    position: "fixed",
    bottom: "16px",
    right: "52px",
    "@media all and (min-width: 1024px)": {
      right: "65px",
    },
  },
  controlAdd: {
    position: "fixed",
    bottom: "82px",
    right: "56px",
    "@media all and (min-width: 1024px)": {
      right: "69px",
    },
  },
  controlAttach: {
    position: "fixed",
    bottom: "140px",
    right: "56px",
    "@media all and (min-width: 1024px)": {
      right: "69px",
    },
  },
  controlBatchRange: {
    position: "fixed",
    bottom: "198px",
    right: "56px",
    "@media all and (min-width: 1024px)": {
      right: "69px",
    },
  },
  linkCell: {
    textDecoration: "none !important",
    color: "#3f51b5 !important",
  },
  stateCell: {
    display: "flex",
    alignItems: "center",
  },
  stateCellIcon: {
    marginRight: "12px",
  },
}));

const defaultConfirmationDialogData = {
  open: false,
  title: "",
  message: "",
  onClose: () => {},
  onDisagree: () => {},
  onAgree: () => {},
};

function Users(props) {
  const classes = useStyles();
  const history = useHistory();

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [confirmationDialogData, setConfirmationDialogData] = useState(
    defaultConfirmationDialogData
  );

  const StateEnum = {
    UNCONFIRMED: "UNCONFIRMED",
    CONFIRMED: "CONFIRMED",
    ARCHIVED: "ARCHIVED",
    COMPROMISED: "COMPROMISED",
    UNKNOWN: "UNKNOWN",
    RESET_REQUIRED: "RESET_REQUIRED",
    FORCE_CHANGE_PASSWORD: "FORCE_CHANGE_PASSWORD",
  };
  const statusCellData = {
    [StateEnum.UNCONFIRMED]: {
      icon: (
        <HourglassEmptyIcon
          className={classes.stateCellIcon}
          color="secondary"
        />
      ),
      label: "Não confirmado",
    },
    [StateEnum.CONFIRMED]: {
      icon: (
        <CheckCircleOutlineIcon
          className={classes.stateCellIcon}
          style={{ color: "#4caf50" }}
        />
      ),
      label: "Confirmado",
    },
    [StateEnum.ARCHIVED]: {
      icon: (
        <ErrorOutlineIcon className={classes.stateCellIcon} color="primary" />
      ),
      label: "Arquivado",
    },
    [StateEnum.COMPROMISED]: {
      icon: (
        <PowerSettingsNewIcon
          className={classes.stateCellIcon}
          style={{ color: "#4caf50" }}
        />
      ),
      label: "Comprometido",
    },
    [StateEnum.UNKNOWN]: {
      icon: (
        <PowerSettingsNewIcon
          className={classes.stateCellIcon}
          color="secondary"
        />
      ),
      label: "Desconhecido",
    },
    [StateEnum.RESET_REQUIRED]: {
      icon: <BlockIcon className={classes.stateCellIcon} color="secondary" />,
      label: "Redefinição de senha necessária",
    },
    [StateEnum.FORCE_CHANGE_PASSWORD]: {
      icon: <BlockIcon className={classes.stateCellIcon} color="secondary" />,
      label: "Forçar mudança de senha",
    },
  };

  const columns = [
    {
      id: "userName",
      label: "Login",
      minWidth: 60,
      render: renderUserNameCell,
    },
    { id: "name", label: "Nome", minWidth: 80, render: renderNameCell },
    {
      id: "email",
      label: "E-mail",
      minWidth: 30,
      align: "left",
      render: renderEmailCell,
    },
    /* custom:last_access attribute not implemented in Cognito.
    {
      id: "lastAccess",
      label: "Último Acesso",
      minWidth: 30,
      align: "left",
      render: renderLastAccessCell,
    }, */
    {
      id: "status",
      label: "Status",
      minWidth: 30,
      align: "left",
      render: renderStatusCell,
    },
    {
      id: "menu",
      type: "menu",
      label: "",
      minWidth: 30,
      align: "right",
    },
  ];

  function generateRowMenu(user) {
    const menu = [];

    if (user.UserStatus === "FORCE_CHANGE_PASSWORD") {
      menu.push({
        label: "Reenviar Convite",
        icon: SendIcon,
        onClick: handleResendInvite,
      });
    }

    Array.prototype.push.apply(menu, [
      {
        label: "Editar",
        icon: EditIcon,
        onClick: handleEditRow,
      },
      {
        label: "Excluir",
        icon: DeleteIcon,
        onClick: handleDeleteRow,
      },
    ]);

    return menu;
  }

  function closeConfirmationDialog() {
    setConfirmationDialogData({ ...confirmationDialogData, open: false });
    setTimeout(() => {
      setConfirmationDialogData(defaultConfirmationDialogData);
    }, 500);
  }

  function renderNameCell(name) {
    return <span>{name}</span>;
  }

  function renderUserNameCell(userName) {
    return (
      <Link className={classes.linkCell} to={`/usuarios/${userName}`}>
        {userName}
      </Link>
    );
  }

  function renderEmailCell(email) {
    return <span>{email}</span>;
  }

  function renderStatusCell(status) {
    const data = statusCellData[status];
    return (
      <span className={classes.stateCell}>
        {data.icon}
        {data.label}
      </span>
    );
  }

  function renderLastAccessCell(lastAccess) {
    if (!lastAccess) {
      return "---";
    }
    return moment(lastAccess).format(USER_DATETIME_FORMAT);
  }

  function loadUsers() {
    setLoadingUsers(true);
    setRows([]);

    setSelectedRows([]);
    User.getUsers()
      .then((data) => {
        setRows(processUsersData(data));
      })
      .catch(() => {
        toast.error("Erro ao carregar lista de usuários!");
      })
      .finally(() => {
        setLoadingUsers(false);
      });
  }

  function processUsersData(data) {
    return data.map((user) => {
      return {
        id: user.Username,
        name: UserUtil.findAttribute(user, "name"),
        userName: user.Username,
        email: UserUtil.findAttribute(user, "email"),
        status: user.UserStatus,
        /* custom:last_access attribute not implemented in Cognito.
        lastAccess: UserUtil.findAttribute(user, "custom:last_access"), */
        menu: generateRowMenu(user),
      };
    });
  }

  function deleteUser(id) {
    User.deleteUser(id)
      .then(() => {
        toast.success("Usuário excluído!");
        loadUsers();
      })
      .catch(() => {
        toast.error("Erro ao excluir usuário!");
      });
  }

  function deleteMultipleUsers(rows) {
    const promises = rows.map((row) => {
      return User.deleteUser(row.id)
        .then(() => {
          toast.success(`Usuário "${row.id}" excluído!`);
          return true;
        })
        .catch(() => {
          toast.error(`Erro ao excluir usuário "${row.id}"!`);
          return false;
        });
    });
    Promise.all(promises).then(() => {
      loadUsers();
    });
  }

  function resendInvitation(row) {
    User.resendInvitation(row.userName)
      .then(() => {
        toast.success("Convite enviado!");
      })
      .catch(() => {
        toast.error("Erro ao enviar convite!");
      });
  }

  function handleDeleteRow(row) {
    setConfirmationDialogData({
      open: true,
      title: "Excluir usuário",
      message: `Você tem certeza que deseja excluir o usuário "${row.id}"?`,
      onClose: () => {
        closeConfirmationDialog();
      },
      onDisagree: () => {
        closeConfirmationDialog();
      },
      onAgree: () => {
        closeConfirmationDialog();
        deleteUser(row.id);
      },
    });
  }

  function handleDeleteMultipleRows(rows) {
    setConfirmationDialogData({
      open: true,
      title: "Excluir usuários",
      message: `Você tem certeza que deseja excluir o(s) usuário(s) selecionado(s)?`,
      onClose: () => {
        closeConfirmationDialog();
      },
      onDisagree: () => {
        closeConfirmationDialog();
      },
      onAgree: () => {
        closeConfirmationDialog();
        deleteMultipleUsers(rows);
      },
    });
  }

  function handleSelectRowUser(rows) {
    setSelectedRows(rows);
  }

  function handleEditRow(row) {
    history.push(`/usuarios/${row.userName}`);
  }

  function handleResendInvite(row) {
    setConfirmationDialogData({
      open: true,
      title: "Reenviar convite",
      message: `Você tem certeza que deseja reenviar convite para o usuário "${row.id}"?`,
      onClose: () => {
        closeConfirmationDialog();
      },
      onDisagree: () => {
        closeConfirmationDialog();
      },
      onAgree: () => {
        closeConfirmationDialog();
        resendInvitation(row);
      },
    });
  }

  function handleRefreshRowsUsers() {
    loadUsers();
  }

  function handleAddUser() {
    history.push("/usuarios/novo");
  }

  function handleCancelUsersMaintenanceDialog() {
    history.push("/usuarios");
  }

  function handleSuccessUsersMaintenanceDialog() {
    history.push("/usuarios");
    loadUsers();
  }

  useEffect(() => {
    loadUsers();
  }, []);

  return (
    <AdminPageTemplate breadcrumbs={props.breadcrumbs}>
      <PageBody>
        {/* table users */}

        <CrudTable
          title="Usuários"
          columns={columns}
          rows={rows}
          rowsSelection={true}
          selectedRows={selectedRows}
          loading={loadingUsers}
          filter={true}
          filterFields={[
            {
              type: "text",
              name: "userName",
              label: "Login",
            },
            {
              type: "text",
              name: "name",
              label: "Nome",
            },
            {
              type: "text",
              name: "email",
              label: "E-mail",
            },
          ]}
          onSelectRow={handleSelectRowUser}
          onDeleteRows={handleDeleteMultipleRows}
          onRefreshRows={handleRefreshRowsUsers}
        />

        {/* end table users */}

        <Fab
          onClick={handleAddUser}
          color="primary"
          aria-label="add"
          className={classes.control}
        >
          <AddIcon />
        </Fab>
      </PageBody>

      {/* maintenance dialog */}
      <Route path="/usuarios/:user">
        <UsersMaintenanceDialog
          open={true}
          onCancel={handleCancelUsersMaintenanceDialog}
          onSuccess={handleSuccessUsersMaintenanceDialog}
        />
      </Route>
      {/* end maintenance dialog */}

      {/* confirmation dialog */}
      <ConfirmationDialog {...confirmationDialogData} />
      {/* end confirmation dialog */}
    </AdminPageTemplate>
  );
}

Users.propTypes = {
  breadcrumbs: PropTypes.array,
};

Users.defaultProps = {
  breadcrumbs: [],
};

export default Users;
